import { defineComponent, computed, ref, useTemplateRef, watch, createBlock, openBlock, unref, withCtx, createElementVNode, createElementBlock, createCommentVNode, createVNode, toDisplayString, createTextVNode, Fragment, renderList, normalizeStyle, withDirectives, normalizeClass, vShow, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import UFormField from "@nuxt/ui/components/FormField.vue";
import UInput from "@nuxt/ui/components/Input.vue";
import UInputMenu from "@nuxt/ui/components/InputMenu.vue";
import UButton from "@nuxt/ui/components/Button.vue";
import { Icon } from "@iconify/vue";
import { POS_TONES, NEUTRAL_HEX, swatchToneHex } from "app-theme";
import CodSheet from "./index28.js";
const _hoisted_1 = { class: "flex flex-col gap-4 p-4" };
const _hoisted_2 = {
  key: 0,
  class: "rounded-input bg-muted px-3 py-2 text-body text-muted",
  "data-test": "convert-notice"
};
const _hoisted_3 = { class: "grid gap-2 grid-cols-[repeat(auto-fit,minmax(40px,1fr))]" };
const _hoisted_4 = ["aria-label", "title", "onClick"];
const _hoisted_5 = ["aria-label", "title"];
const _hoisted_6 = {
  key: 3,
  "data-test": "advanced-section"
};
const _hoisted_7 = {
  key: 0,
  class: "mt-3 flex flex-col gap-3"
};
const _hoisted_8 = { class: "grid grid-cols-4 gap-2" };
const _hoisted_9 = ["aria-label", "title"];
const _hoisted_10 = { class: "w-full truncate text-center text-helper leading-tight" };
const _hoisted_11 = ["aria-label", "title"];
const _hoisted_12 = { class: "w-full truncate text-center text-helper leading-tight text-muted" };
const _hoisted_13 = ["onClick"];
const _hoisted_14 = ["src"];
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "SalesButtonConfigModal",
  props: {
    modelValue: {
      type: Boolean,
      required: false
    },
    text: {
      type: String,
      default: ""
    },
    /** Current background_color of the edited button (preselects its tone). */
    color: {
      type: String,
      default: ""
    },
    /** Hide the tone strip (admin product buttons are name-only). */
    showTones: {
      type: Boolean,
      default: true
    },
    /** Reveal the advanced image section (admin hospitality editor only). */
    showImage: {
      type: Boolean,
      default: false
    },
    /** Image library (host-owned; uploaded entries are unshifted by the host). */
    images: {
      type: Array,
      default: () => []
    },
    /** Current image of the edited button (path + url), if any. */
    image: {
      type: String,
      default: null
    },
    imageUrl: {
      type: String,
      default: null
    },
    /** Non-empty when editing a v1 button: the edit-converts notice copy. */
    convertNotice: {
      type: String,
      default: ""
    },
    /** Footer Delete action (edit hosts) — emits `delete`, host owns the rest. */
    showDelete: {
      type: Boolean,
      default: false
    },
    /** Create-at-cell hosts: the unplaced categories/products to pick from.
     *  Non-null renders the picker and gates Save on a selection. */
    pickerItems: {
      type: Array,
      default: null
    },
    pickerLabel: {
      type: String,
      default: ""
    }
  },
  emits: ["update:modelValue", "confirm", "upload-image", "delete"],
  setup(__props, { emit: __emit }) {
    const props = __props;
    const { t } = useI18n();
    const emit = __emit;
    const isOpen = computed({
      get: () => props.modelValue,
      set: (v) => emit("update:modelValue", v)
    });
    const toneKeys = Object.keys(POS_TONES);
    const selected = ref(POS_TONES.cigla);
    const btnText = ref("");
    const advancedOpen = ref(false);
    const submitted = ref(false);
    const selectedImage = ref(null);
    const pickedItem = ref(void 0);
    const inputUpload = useTemplateRef("inputUpload");
    const pickerWrap = useTemplateRef("pickerWrap");
    const onPick = (item) => {
      if (!item) return;
      btnText.value = item.name;
      nextTick(() => {
        var _a, _b;
        return (_b = (_a = document.activeElement) == null ? void 0 : _a.blur) == null ? void 0 : _b.call(_a);
      });
    };
    const onModalPresent = () => {
      submitted.value = false;
      pickedItem.value = void 0;
      if (props.pickerItems) {
        setTimeout(() => {
          var _a, _b;
          if (props.modelValue) (_b = (_a = pickerWrap.value) == null ? void 0 : _a.querySelector("input")) == null ? void 0 : _b.focus();
        }, 300);
      }
      btnText.value = props.text;
      selected.value = props.color ? swatchToneHex(props.color) : POS_TONES.cigla;
      selectedImage.value = props.image || props.imageUrl ? { path: props.image, image_url: props.imageUrl } : null;
      advancedOpen.value = selectedImage.value !== null;
    };
    watch(() => props.modelValue, (val) => {
      if (val) onModalPresent();
    }, { immediate: true });
    const onUploadFile = (event) => {
      const files = event.target.files;
      if (!files || !files.length) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        var _a;
        emit("upload-image", { name: files[0].name, file: (_a = e.target) == null ? void 0 : _a.result });
      };
      reader.readAsDataURL(files[0]);
    };
    const handleDismiss = () => {
      emit("update:modelValue", false);
    };
    const onDelete = () => {
      if (submitted.value) return;
      submitted.value = true;
      emit("delete");
      handleDismiss();
    };
    const confirm = () => {
      var _a, _b;
      if (submitted.value) return;
      if (props.pickerItems && !pickedItem.value) return;
      submitted.value = true;
      const result = {
        text: btnText.value,
        backgroundColor: selected.value,
        version: 2
      };
      if (props.showImage) {
        result.image = ((_a = selectedImage.value) == null ? void 0 : _a.path) ?? null;
        result.imageUrl = ((_b = selectedImage.value) == null ? void 0 : _b.image_url) ?? null;
      }
      if (props.pickerItems && pickedItem.value) {
        result.itemId = pickedItem.value.id;
      }
      emit("confirm", result);
      handleDismiss();
    };
    return (_ctx, _cache) => {
      return openBlock(), createBlock(CodSheet, {
        modelValue: isOpen.value,
        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => isOpen.value = $event),
        "show-header": "",
        title: unref(t)("general.settings"),
        width: "542px"
      }, {
        footer: withCtx(() => [
          __props.showDelete ? (openBlock(), createBlock(UButton, {
            key: 0,
            size: "lg",
            color: "error",
            variant: "outline",
            "data-test": "delete-button",
            onClick: onDelete
          }, {
            default: withCtx(() => [
              createTextVNode(toDisplayString(unref(t)("order.button_remove")), 1)
            ]),
            _: 1
          })) : createCommentVNode("", true),
          createVNode(UButton, {
            size: "lg",
            color: "primary",
            variant: "solid",
            disabled: !!__props.pickerItems && !pickedItem.value,
            "data-test": "save-button",
            onClick: confirm
          }, {
            default: withCtx(() => [
              createTextVNode(toDisplayString(unref(t)("general.save")), 1)
            ]),
            _: 1
          }, 8, ["disabled"])
        ]),
        default: withCtx(() => [
          createElementVNode("div", _hoisted_1, [
            __props.convertNotice ? (openBlock(), createElementBlock("div", _hoisted_2, toDisplayString(__props.convertNotice), 1)) : createCommentVNode("", true),
            __props.pickerItems ? (openBlock(), createBlock(UFormField, {
              key: 1,
              label: __props.pickerLabel,
              "data-test": "picker"
            }, {
              default: withCtx(() => [
                createElementVNode("div", {
                  ref_key: "pickerWrap",
                  ref: pickerWrap
                }, [
                  createVNode(UInputMenu, {
                    modelValue: pickedItem.value,
                    "onUpdate:modelValue": [
                      _cache[0] || (_cache[0] = ($event) => pickedItem.value = $event),
                      onPick
                    ],
                    items: __props.pickerItems,
                    portal: false,
                    "open-on-focus": "",
                    virtualize: "",
                    "label-key": "name",
                    class: "w-full",
                    ui: { content: "z-50" }
                  }, {
                    empty: withCtx(() => [
                      createTextVNode(toDisplayString(unref(t)("order_settings.no_match_found")), 1)
                    ]),
                    _: 1
                  }, 8, ["modelValue", "items"])
                ], 512)
              ]),
              _: 1
            }, 8, ["label"])) : createCommentVNode("", true),
            createVNode(UFormField, {
              label: unref(t)("order.button_name")
            }, {
              default: withCtx(() => [
                createVNode(UInput, {
                  modelValue: btnText.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => btnText.value = $event),
                  class: "w-full"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }, 8, ["label"]),
            __props.showTones ? (openBlock(), createBlock(UFormField, {
              key: 2,
              label: unref(t)("order.button_color")
            }, {
              default: withCtx(() => [
                createElementVNode("div", _hoisted_3, [
                  (openBlock(true), createElementBlock(Fragment, null, renderList(unref(toneKeys), (toneKey) => {
                    return openBlock(), createElementBlock("button", {
                      key: toneKey,
                      type: "button",
                      class: "flex aspect-square w-full cursor-pointer items-center justify-center rounded-input text-white",
                      style: normalizeStyle(`background: var(--pos-tone-${toneKey}, ${unref(POS_TONES)[toneKey]})`),
                      "data-test": "tone-swatch",
                      "aria-label": unref(t)(`order.tone.${toneKey}`),
                      title: unref(t)(`order.tone.${toneKey}`),
                      onClick: ($event) => selected.value = unref(POS_TONES)[toneKey]
                    }, [
                      selected.value === unref(POS_TONES)[toneKey] ? (openBlock(), createBlock(unref(Icon), {
                        key: 0,
                        icon: "lucide:check",
                        class: "size-6"
                      })) : createCommentVNode("", true)
                    ], 12, _hoisted_4);
                  }), 128)),
                  createElementVNode("button", {
                    type: "button",
                    class: "flex aspect-square w-full cursor-pointer items-center justify-center rounded-input border border-accented bg-default text-default",
                    "aria-label": unref(t)("order.tone.neutral"),
                    title: unref(t)("order.tone.neutral"),
                    onClick: _cache[2] || (_cache[2] = ($event) => selected.value = unref(NEUTRAL_HEX))
                  }, [
                    selected.value === unref(NEUTRAL_HEX) ? (openBlock(), createBlock(unref(Icon), {
                      key: 0,
                      icon: "lucide:check",
                      class: "size-6"
                    })) : createCommentVNode("", true)
                  ], 8, _hoisted_5)
                ])
              ]),
              _: 1
            }, 8, ["label"])) : createCommentVNode("", true),
            __props.showImage ? (openBlock(), createElementBlock("div", _hoisted_6, [
              createVNode(UButton, {
                color: "neutral",
                variant: "ghost",
                size: "sm",
                icon: advancedOpen.value ? "i-lucide-chevron-down" : "i-lucide-chevron-right",
                "data-test": "advanced-toggle",
                onClick: _cache[3] || (_cache[3] = ($event) => advancedOpen.value = !advancedOpen.value)
              }, {
                default: withCtx(() => [
                  createTextVNode(toDisplayString(unref(t)("order_settings.advanced_image")), 1)
                ]),
                _: 1
              }, 8, ["icon"]),
              advancedOpen.value ? (openBlock(), createElementBlock("div", _hoisted_7, [
                createElementVNode("div", _hoisted_8, [
                  createElementVNode("button", {
                    type: "button",
                    class: "flex h-20 w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-input border border-dashed border-accented px-1 text-dimmed",
                    "aria-label": unref(t)("order_settings.upload_image"),
                    title: unref(t)("order_settings.upload_image"),
                    "data-test": "image-upload",
                    onClick: _cache[4] || (_cache[4] = ($event) => {
                      var _a;
                      return (_a = inputUpload.value) == null ? void 0 : _a.click();
                    })
                  }, [
                    createVNode(unref(Icon), {
                      icon: "lucide:upload",
                      class: "size-5 shrink-0"
                    }),
                    createElementVNode("span", _hoisted_10, toDisplayString(unref(t)("order_settings.upload_image")), 1)
                  ], 8, _hoisted_9),
                  createElementVNode("button", {
                    type: "button",
                    class: normalizeClass(["flex h-20 w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-input border px-1", selectedImage.value === null ? "border-primary border-2" : "border-default"]),
                    "aria-label": unref(t)("order_settings.no_image"),
                    title: unref(t)("order_settings.no_image"),
                    "data-test": "image-none",
                    onClick: _cache[5] || (_cache[5] = ($event) => selectedImage.value = null)
                  }, [
                    createVNode(unref(Icon), {
                      icon: "lucide:ban",
                      class: "size-5 shrink-0 text-dimmed"
                    }),
                    createElementVNode("span", _hoisted_12, toDisplayString(unref(t)("order_settings.no_image")), 1)
                  ], 10, _hoisted_11),
                  (openBlock(true), createElementBlock(Fragment, null, renderList(__props.images, (img, index) => {
                    return openBlock(), createElementBlock("button", {
                      key: index,
                      type: "button",
                      class: normalizeClass(["h-20 w-full cursor-pointer overflow-hidden rounded-input border", selectedImage.value && selectedImage.value.image_url === img.image_url ? "border-primary border-2" : "border-default"]),
                      "data-test": "image-option",
                      onClick: ($event) => selectedImage.value = img
                    }, [
                      createElementVNode("img", {
                        src: img.image_url ?? void 0,
                        class: "size-full object-contain"
                      }, null, 8, _hoisted_14)
                    ], 10, _hoisted_13);
                  }), 128))
                ]),
                withDirectives(createElementVNode("input", {
                  ref_key: "inputUpload",
                  ref: inputUpload,
                  type: "file",
                  accept: "image/*",
                  onChange: onUploadFile
                }, null, 544), [
                  [vShow, false]
                ])
              ])) : createCommentVNode("", true)
            ])) : createCommentVNode("", true)
          ])
        ]),
        _: 1
      }, 8, ["modelValue", "title"]);
    };
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=index13.js.map
